import * as Sentry from '@sentry/browser';

/**
 * Basic click function
 * @param {String} elQuery query selector
 * @param {any} func function name calls on click
 * @param {any} param optional parametr
 */
export function click(elQuery, func, param = false) {
  var elList = document.querySelectorAll(elQuery);
  for (var i = 0, len = elList.length; i < len; i++) {
    elList[i].addEventListener('click', (e) => {
      if (param) {
        func(param);
      } else {
        func(e.currentTarget);
      }
    });
  }
}

/**
 * Simulate a click event.
 * @param {Element} el element to simulate a click on
 */
export function simulateClick(el) {
  var evt = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window
  });

  if (!el) {
    return;
  }
  el.dispatchEvent(evt);
}

const sentryBaseUrlName = (url) => {
  let urlModifed = url
    .replace(/^https?:\/\//, '')
    .replace(/(\d+)(?=\.(com|io))/, '')
    .split('?')[0];

  const targets = ['games-data/'];
  for (const target of targets) {
    const [before, after] = urlModifed.split(target);

    urlModifed = after !== undefined ? `${before}${target}` : urlModifed;
  }

  return urlModifed;
};

function buildQueryString(params) {
  return Object.entries(params)
    .map((d) => `${d[0]}=${d[1]}`)
    .join('&');
}

/**
 * Get Data
 * @param {String} url JSON Url
 * @param {Object} queryParams Params to build a query
 * @returns {Promise}
 */
export function getData(url, queryParams, availableStates = []) {
  return new Promise((resolve, reject) => {
    const qs = queryParams ? '?' + buildQueryString(queryParams) : '';
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${url}${qs}`, true);
    xhr.withCredentials = true;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 400 && xhr.responseText != '') {
        resolve({ response: xhr.responseText, status: true });
      } else {
        if (!availableStates.includes(xhr.status)) {
          const errorCode = xhr.status || '0';
          const message = `[GET] [${sentryBaseUrlName(url)}] [STATUS ${errorCode}]`;

          Sentry.setExtra('details', {
            url: `${url}${qs}`,
            response: { status: xhr.status, body: xhr.responseText }
          });
          Sentry.captureMessage(message);
        }

        resolve({ response: xhr.responseText, status: false });
      }
    };
    xhr.onerror = () => reject(xhr.statusText);
    xhr.send();
  });
}

/**
 * Get JSON
 * @param {String} url JSON Url
 * @param {Object} queryParams Params to build a query
 * @returns {Promise}
 */
export function getJSON(url, queryParams = undefined, availableStates) {
  return new Promise((resolve, reject) => {
    getData(url, queryParams, availableStates)
      .then(({ response, status }) => {
        try {
          if (status) {
            resolve(JSON.parse(response));
          } else {
            resolve(status);
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          const qs = queryParams ? '?' + buildQueryString(queryParams) : '';

          const message = `JSON PARSE ERROR [${sentryBaseUrlName(url)}]`;

          Sentry.setExtra('details', {
            url: `${url}${qs}`,
            response: { status: status, body: response }
          });
          Sentry.captureMessage(message);

          resolve(status);
        }
      })
      .catch(reject);
  });
}

/**
 * Check if data in localstorage exists and no older than 1 hour
 * @param {String} key localStorage key
 * @returns {Boolean}
 */
export function checkLocalStorage(key, minutes) {
  // todo if body class dev-mode then always false
  /* if (typeof _DEV_MODE !== 'undefined') {
    return false;
  } */

  try {
    if (localStorage.hasOwnProperty(key)) {
      let gamesObj = JSON.parse(localStorage.getItem(key)),
        localStorageGamesDate = gamesObj.timestamp,
        now = new Date().getTime().toString(),
        timestamp_newStructure = 1669022635431;

      if (localStorageGamesDate + (minutes || 60) * 60 * 1000 > now && localStorageGamesDate > timestamp_newStructure) {
        return true;
      }
    }
  } catch (error) {
    console.error(error);
    return false;
  }

  return false;
}

/**
 * Set item into localStorage
 * @param {String} key localStorage key
 * @param {Object} data
 */
export function setLocalStorageItem(key, data) {
  //if(typeof _DEV_MODE === 'undefined') {
  var cAllow = false;
  try {
    localStorage.setItem(key, JSON.stringify(data));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    if (getFromLocalStorage('c_allow')) {
      cAllow = true;
    }
    localStorage.clear();
    try {
      if (cAllow) {
        localStorage.setItem('c_allow', '1');
      }
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error(e);
    }
  }
  //}
}

/**
 * Set item into sessionStorage
 * @param {String} key sessionStorage key
 * @param {Object} data
 */
export function setSessionStorageItem(key, data) {
  //if(typeof _DEV_MODE === 'undefined') {
  let cAllow = false;
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    if (getFromSessionStorage('c_allow')) {
      cAllow = true;
    }
    sessionStorage.clear();
    try {
      if (cAllow) {
        sessionStorage.setItem('c_allow', '1');
      }
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error(e);
    }
  }
  //}
}

/**
 * Get data from localStorage
 * @param {String} key localStorage key
 * @returns {JSON}
 */
export function getFromLocalStorage(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    console.error('Error parsing localStorage data for key:', key, error);
    return undefined;
  }
}

/**
 * Get data from sessionStorage
 * @param {String} key sessionStorage key
 * @returns {JSON}
 */
export function getFromSessionStorage(key) {
  try {
    return JSON.parse(sessionStorage.getItem(key));
  } catch (error) {
    console.error('Error parsing sessionStorage data for key:', key, error);
    return undefined;
  }
}
/**
 * Sort array of objects based on another array
 * @param {Array} array array to sort
 * @param {Array} order array with order
 * @param {String} key
 * @returns {Array}
 */
export function arrayOrder(array, order, key) {
  array.sort((a, b) => {
    var A = a[key],
      B = b[key];
    if (order.indexOf(A) > order.indexOf(B)) {
      return 1;
    } else {
      return -1;
    }
  });
  return array;
}

export function createEl(domstring) {
  const html = new DOMParser().parseFromString(domstring, 'text/html');
  return html.body.firstChild;
}

export function decodeHTML(html) {
  let temp = document.createElement('textarea');
  temp.innerHTML = html;
  return temp.value;
}

let isSharingInProgress = false;
export async function urlShare(el) {
  const langLocale = _CMS_LOCALE != '' ? _CMS_LOCALE : _CMS_LANGUAGE,
    slug = el.getAttribute('data-slug'),
    url = slug ? `${window.location.origin}/${langLocale}/slots/${slug}` : window.location.href;

  const shareData = {
    title: el.getAttribute('data-share-title'),
    url: url
  };

  if (!isSharingInProgress) {
    try {
      isSharingInProgress = true;
      await navigator.share(shareData);
      isSharingInProgress = false;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      await navigator.clipboard.writeText(url).catch(() => {});
      isSharingInProgress = false;
    }
  }
}

/**
 * has func
 * @param {NodeList} nodeList
 * @param {String} selector
 * @returns {Array}
 */
export function has(nodeList, selector) {
  return Array.from(nodeList).filter((e) => e.querySelector(selector));
}

export function isToday(date) {
  const today = new Date();
  return (
    date.getDate() == today.getDate() &&
    date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear()
  );
}

export function isTomorrow(date) {
  const today = new Date();
  var tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  return (
    tomorrow.getFullYear() == date.getFullYear() &&
    tomorrow.getMonth() == date.getMonth() &&
    tomorrow.getDate() == date.getDate()
  );
}

export function isLessThanXDays(date, x) {
  const today = new Date();
  var futureDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + x);
  date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return date > today && date <= futureDate;
}

export function getDay(date) {
  return date.toLocaleDateString(_CMS_LANGUAGE, {
    weekday: 'long'
  });
}

export function getUrlLastSegment() {
  var url = document.URL.replace(/\/$/, '');
  var lastSegment = url.substring(url.lastIndexOf('/') + 1);
  lastSegment = lastSegment.replace('#', '').replace('?', '');
  return lastSegment;
}

export function getWalletURL(path = '', prefix = 'wallet') {
  return getAPIURL(path, prefix, false);
}

export function getUserURL(path = '', prefix = 'user') {
  return getAPIURL(path, prefix, false);
}

export function getAPIURL(path = '', prefix, isCMS = false) {
  const walletURL = `https://${prefix ? `${prefix}.` : ''}energycasino${
    _CMS_USER_DOMAIN != 0 ? _CMS_USER_DOMAIN : ''
  }.com/${path}`;
  if (
    !document.querySelector('#input-dev-env-domain') ||
    (_CMS_WALLET_PROD && !document.querySelector('#input-dev-env-domain')?.value)
  )
    return walletURL;
  const envID = isCMS ? false : document.querySelector('#input-dev-env-domain').value || 'dev';
  return `https://${prefix ? `${prefix}-` : ''}${envID ? `${envID}.` : ''}${
    isCMS ? window.location.host : `testowaplatforma123.net`
  }/${path}`;
}

export function randomNumber(min, max) {
  return Math.floor(Math.random() * (max - min) + min);
}

var _ACTIVE_FETCH = {};
window['ACTIVE_FETCH'] = _ACTIVE_FETCH;

export async function startFetch(id) {
  if (!id) return false;

  const data = _ACTIVE_FETCH[id],
    now = new Date().getTime();

  if (!!data && !data.finished && now - data?.timestamp < 2000) {
    let i = 0;
    const tryFetch = async () => {
      i++;
      if (_ACTIVE_FETCH[id]?.finished || i >= 20) return true;
      return new Promise((r) => setTimeout(async () => r(await tryFetch()), 50));
    };
    return await tryFetch();
  }
  _ACTIVE_FETCH[id] = {
    timestamp: now,
    finished: false
  };
  return false;
}

export async function stopFetch(id) {
  if (!_ACTIVE_FETCH[id]) return false;
  _ACTIVE_FETCH[id].finished = true;
}

export function runByLoginControl(funcLogged, funcUnlogged) {
  if (!document.body.classList.contains('js-login-control')) return;

  const checkStatus = setInterval(() => {
    const classes = document.body.classList;
    if (classes.contains('js-loggedin') || classes.contains('js-anonymous')) {
      try {
        if (funcUnlogged && classes.contains('js-anonymous')) {
          funcUnlogged();
        }
        if (funcLogged && classes.contains('js-loggedin')) {
          funcLogged();
        }
      } catch (error) {
        console.error('There was an error running one of the functions:', error);
      } finally {
        clearInterval(checkStatus);
      }
    }
  }, 50);
}

export function initLoginStatus() {
  _CMS_USER_SESSION_ACTIVE = checkLoginStatus() ? 1 : 0;
}

export function checkLoginStatus() {
  if (!document.body.classList.contains('js-login-control')) return null;
  if (document.body.classList.contains('js-loggedin') || document.body.classList.contains('js-anonymous')) {
    return document.body.classList.contains('js-loggedin');
  }

  return new Promise((resolve, reject) => {
    let timeoutReached = false;

    const timeout = setTimeout(() => {
      timeoutReached = true;
      clearInterval(checkStatus);
      reject(new Error('Timeout reached without determining login status.'));
    }, 5000);

    const checkStatus = setInterval(() => {
      if (timeoutReached) return;

      const classes = document.body.classList;
      if (classes.contains('js-loggedin') || classes.contains('js-anonymous')) {
        clearTimeout(timeout);
        clearInterval(checkStatus);

        try {
          if (classes.contains('js-anonymous')) {
            resolve(false);
          } else if (classes.contains('js-loggedin')) {
            resolve(true);
          }
        } catch (error) {
          console.error('There was an error checking the login status:', error);
          reject(error);
        }
      }
    }, 50);
  });
}

export function createSafeRegExp(string) {
  const escapedString = string.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, '\\$&');

  return new RegExp(escapedString, 'i');
}
